<template>
  <panel-layout
    id="message-panel"
    :loading="loading"
    :show-footer="false"
  >
    <template #heading>
      <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85 mb-1">
        {{ $t('pages.marketing.reviews.review.title') }}
      </h2>
    </template>

    <template #actions-right>
      <div v-if="review" class="flex items-center space-x-1">
        <o-tooltip :text="$t('review.reply')">
          <o-button icon="reply" flat @click="handleReply" />
        </o-tooltip>

        <o-tooltip v-if="!review.approved" :text="$t('review.approve')">
          <o-button icon="approve" flat @click="handleApprove" />
        </o-tooltip>

        <o-tooltip v-if="review.approved" :text="$t('review.unapprove')">
          <o-button icon="cross" flat @click="handleUnapprove" />
        </o-tooltip>

        <o-tooltip v-if="!review.featured" :text="$t('review.feature')">
          <o-button icon="promote" flat @click="handleFeature" />
        </o-tooltip>

        <o-tooltip v-if="review.featured" :text="$t('review.unfeature')">
          <o-button icon="promote" flat @click="handleUnfeature" />
        </o-tooltip>

        <o-tooltip :text="$t('general.delete')">
          <o-button icon="delete" flat @click="handleDelete" />
        </o-tooltip>
      </div>
    </template>

    <div v-if="review" class="h-full">
      <o-scroll class="bg-gray-50 dark:bg-gray-900">
        <div class="h-full flex flex-col">
          <div class="flex flex-col divide-y py-3">
            <div>
              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.customer') }}
                </p>
                <div class="flex-grow">
                  <div v-if="review.customer" class="w-full flex items-center text-left">
                    <o-avatar v-if="review.displayName" :items="[review.customer]" size="md" border="" class="flex-shrink-0 mr-2" />
                    <div class="text-sm font-normal">
                      <p class="leading-none">
                        {{ review.customer.displayName }}

                        <span v-if="!review.displayName">
                          {{ $t('review.nameHidden') }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('review.rating') }}
                </p>
                <div class="flex-grow">
                  <div v-if="review.rating !== null" class="flex items-center">
                    <svg
                      v-for="index in 5"
                      :key="index"
                      :class="[
                        'w-5 h-5',
                        review.rating >= index ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-800',
                      ]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>

                  <span v-else>-</span>
                </div>
              </div>

              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('pages.marketing.reviews.review.dateTime') }}
                </p>
                <div class="flex-grow">
                  {{ $date.format(review.created * 1000, $date.presets.full) }}
                </div>
              </div>

              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.status') }}
                </p>
                <div class="flex-grow flex items-center space-x-2">
                  <o-chip v-if="review.recommended" variant="success">
                    {{ $t('review.recommended') }}
                  </o-chip>

                  <o-chip v-if="!review.approved" variant="warning">
                    {{ $t('review.unapproved') }}
                  </o-chip>

                  <o-chip v-if="review.approved" variant="success">
                    {{ $t('review.approved') }}
                  </o-chip>

                  <o-chip v-if="review.reply" variant="info">
                    {{ $t('review.replied') }}
                  </o-chip>

                  <o-chip v-if="review.featured" variant="success">
                    {{ $t('review.featured') }}
                  </o-chip>
                </div>
              </div>
            </div>
          </div>
          <div class="m-6 p-3 bg-gray-100 dark:bg-gray-800/20 dark:border rounded-md whitespace-pre-line">
            <p class="font-medium opacity-60 uppercase tracking-wider mb-3 leading-none -mt-2 text-xs">
              {{ $t('pages.marketing.reviews.review.label') }}
            </p>

            <div v-html="review.comment" />
          </div>

          <div v-if="review.reply" class="m-6 p-3 bg-gray-100 dark:bg-gray-800/20 dark:border rounded-md whitespace-pre-line">
            <p class="font-medium opacity-60 uppercase tracking-wider mb-3 leading-none -mt-2 text-xs">
              {{ $t('review.reply') }}
            </p>

            <div v-html="review.reply" />
          </div>
        </div>
      </o-scroll>

      <approve-dialog
        ref="approveDialog"
        v-model:review="review"
        @success="handleSuccess"
      />

      <unapprove-dialog
        ref="unapproveDialog"
        v-model:review="review"
        @success="handleSuccess"
      />

      <feature-dialog
        ref="featureDialog"
        v-model:review="review"
        @success="handleSuccess"
      />

      <unfeature-dialog
        ref="unfeatureDialog"
        v-model:review="review"
        @success="handleSuccess"
      />

      <reply-dialog
        ref="replyDialog"
        v-model:review="review"
        @success="handleSuccess"
      />

      <delete-dialog
        ref="deleteDialog"
        @success="handleDeleteSuccess"
      />
    </div>
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import ApproveDialog from '@/components/review/actions/Approve.vue'
// @ts-ignore
import UnapproveDialog from '@/components/review/actions/Unapprove.vue'
// @ts-ignore
import FeatureDialog from '@/components/review/actions/Feature.vue'
// @ts-ignore
import UnfeatureDialog from '@/components/review/actions/Unfeature.vue'
// @ts-ignore
import ReplyDialog from '@/components/review/actions/Reply.vue'
// @ts-ignore
import DeleteDialog from '@/components/review/actions/Delete.vue'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const { $api, $config, $notification, $bus } = useNuxtApp()

const panel = usePanel()

const review = ref<any>(null)
const loading = ref(true)

const approveDialog = ref<any>()
const unapproveDialog = ref<any>()
const featureDialog = ref<any>()
const unfeatureDialog = ref<any>()
const replyDialog = ref<any>()
const deleteDialog = ref<any>()

const isDev = computed(() => {
  return $config.public.instance === 'dev'
})

const isAdmin = computed(() => {
  return (useNuxtApp() as any).$auth?.info?.adminUser
})

const fetchReview = async () => {
  try {
    const result = (await $api.review.get(props.id)).data

    review.value = result
    loading.value = false
  } catch (e: any) {
    panel?.close()
    $notification.error({ errors: e?.errors })
  }
}

const handleApprove = () => {
  approveDialog.value.open(review.value)
}

const handleUnapprove = () => {
  unapproveDialog.value.open(review.value)
}

const handleFeature = () => {
  featureDialog.value.open(review.value)
}

const handleUnfeature = () => {
  unfeatureDialog.value.open(review.value)
}

const handleReply = () => {
  replyDialog.value.open(review.value)
}

const handleDelete = () => {
  deleteDialog.value.open(review.value)
}

const handleDeleteSuccess = () => {
  panel?.close()
  $bus.emit.deleted('Review', review.value)
}

const handleSuccess = () => {
  $bus.emit.updated('Review', review.value)
}

fetchReview()
</script>
